/**
 * ``useMapSseBridges`` — mounts the live-SSE → shared-store bridges that the
 * Map must keep warm for surfaces other than the map markers themselves.
 *
 * # Why this hook exists (ADR-032 PR6 re-home)
 *
 * Through PR5a/PR5b these three bridges were mounted inside the now-removed
 * :func:`EventLayer` (the data-plane Events glyph layer, superseded by the
 * Detections + Alerts surfaces). Event removal (PR6) deletes that component,
 * so its store-warming responsibilities are re-homed here — a small,
 * non-visual hook mounted once in :file:`Map.tsx` alongside the layer
 * components. Losing these would silently break the live Detections panel,
 * the live Alerts panel + cross-session dismissal fan-out, and the SAPIENT
 * geometry washes — none of which is acceptable, so they move rather than
 * disappear.
 *
 * The Map owns a SINGLE :class:`EventSource` connection (see :func:`useSse`);
 * ``connection.subscribe`` is the one feeder. Mounting all the store-warmers
 * here (next to each other, off that same ``subscribe``) preserves the "one
 * connection, one feeder" invariant the read-only layers rely on —
 * :func:`DetectionLayer`, :func:`AlertLayer`, and the FoV / coverage /
 * obscuration polygon layers READ their stores and must never re-process a
 * delta, so they mount no bridge of their own.
 *
 * Each consumer registers its own callback, and :func:`useSse` fans every
 * message out to all of them synchronously (issue #739). The feeder used to
 * be a single ``lastMessage`` value watched by effects, which delivered only
 * the final message of any burst that landed between two renders.
 *
 *   - :func:`useDetectionsSseBridge` — ``detection`` deltas → detections
 *     store (REQ-QUERY-028); the Detections panel + :func:`DetectionLayer`
 *     read it.
 *   - :func:`useAlertsSseBridge` — ``alert`` + ``alert-dismissed`` deltas →
 *     alerts store (REQ-QUERY-025; ADR-029 [MVP] item 6 cross-session
 *     dismissal fan-out); the Alerts panel + :func:`AlertLayer` read it.
 *   - :func:`useStatusGeometrySseBridge` — each ``status`` frame's projected
 *     ``geometry`` → per-platform geometry store (REQ-UI-070; ADR-032 §4.2);
 *     the FoV / coverage / obscuration washes read it.
 *
 * The dynamic-track bridge (:func:`useTrackSseBridge`) is NOT here — it stays
 * mounted in :func:`TrackTailLayer`, which already owns it.
 *
 * Cross-references:
 *   - ADR-032 (Detection/Alert supersede Event); ADR-029 (Alerts).
 *   - REQ-QUERY-025 / REQ-QUERY-028 / REQ-UI-070 / REQ-UI-006 (live latency).
 *   - map/use-sse.ts (the single SseConnection.subscribe feeder) ·
 *     map/sse-subscription.ts (the fan-out contract).
 *   - map/detection-layer.tsx / map/alert-layer.tsx / map/fov-layer.tsx
 *     (the read-only consumers of the stores warmed here).
 */
import type { SseSubscribe } from "@/map/sse-subscription";
import type { CommitSchedulerOptions } from "@/map/sse-commit-scheduler";
import { useDetectionsSseBridge } from "@/detections/use-detections-sse-bridge";
import { useAlertsSseBridge } from "@/alerts/use-alerts-sse-bridge";
import { useAlertNotifications } from "@/alerts/use-alert-notifications";
import { usePreferences } from "@/preferences/preferences-context";
import { useStatusGeometrySseBridge } from "@/map/use-status-geometry-sse-bridge";
import { useLiveWindowMs } from "@/chrome/use-deployment-config";

/**
 * Mount the detections / alerts / notification / status-geometry SSE
 * consumers against the Map's live stream. Returns nothing — pure
 * side-effect (each child bridge upserts its shared Zustand store; the
 * notification dispatch raises the Flow-F surface).
 *
 * @param subscribe - The Map's ``connection.subscribe`` (the same value
 *     threaded into :func:`SnapshotLayer` / :func:`TrackTailLayer`).
 *     ``null`` mounts no subscriptions.
 * @param options - ``commit``: the detections bridge's commit-scheduler knobs
 *     (tests inject deterministic wakeups; production leaves it undefined).
 */
export function useMapSseBridges(
  subscribe: SseSubscribe | null,
  options: { readonly commit?: CommitSchedulerOptions } = {},
): void {
  // The operator's persisted SAPIENT-alert threshold (ADR-029 §6.1), read at
  // the composition point rather than inside the notification hook so that hook
  // stays explicitly-parameterised and unit-testable without a preferences
  // provider. Absent (a Profile saved before the section existed) ⇒ the ratified
  // `all` default, so an older Profile surfaces MORE alerts, never fewer.
  const threshold = usePreferences().workingState.notifications?.sapient_alert_threshold;
  // ADR-032: keep the shared detections store warm from the live
  // ``sseMessage`` so the Detections panel + DetectionLayer paint within the
  // sub-second latency Flow 02 requires, ahead of the next REST poll.
  // ADR-041 D-7 / REQ-UI-181: the deployment live window is the prune floor
  // of the detections + object-track stores (the REST feeds pass the same).
  const retentionMs = useLiveWindowMs();
  // The detections bridge commits once per animation frame (feature spec
  // ``ui-track-load-headroom`` §2.4; REQ-UI-192); ``options.commit`` lets a
  // test hand-crank the frame.
  useDetectionsSseBridge(subscribe, { retentionMs, commit: options.commit });
  // ADR-029 / ADR-032: keep the shared alerts store warm — both new ``alert``
  // deltas and the cross-session ``alert-dismissed`` fan-out (ADR-029 [MVP]
  // item 6) so all operators drop a dismissed alert together.
  useAlertsSseBridge(subscribe);
  // ADR-029 §5 / REQ-UI-062: raise the Flow-F toast / sticky banner for an
  // arriving alert, and retract it on the cross-session dismissal fan-out.
  // Separate from the bridge above so that one stays a provider-free store
  // warmer; this one needs the dismiss mutation + session role + selection bus.
  useAlertNotifications(subscribe, threshold === undefined ? {} : { threshold });
  // ADR-032 §4.2 (REQ-UI-070): route each ``status`` frame's projected
  // ``geometry`` (field-of-view / obscuration / coverage rings) into the
  // per-platform geometry store. The polygon layers READ that store; the
  // single SSE subscription is the one feeder so they never re-process a delta.
  useStatusGeometrySseBridge(subscribe);
}
